/**
 * ElevenLabs Text-to-Speech Integration Module
 * Provides high-quality voice synthesis using ElevenLabs API
 * 
 * Features:
 * - Multi-language support
 * - Audio caching for performance
 * - Automatic fallback to Web Speech API
 * - Memory management
 */

const ElevenLabsTTS = {
    // Configuration
    config: {
        voiceId: 'gD1IexrzCvsXPHUuT0s3',
        modelId: 'eleven_multilingual_v2',
        endpoint: 'https://api.elevenlabs.io/v1/text-to-speech',
        voiceSettings: {
            stability: 0.35,              // Más bajo = más expresivo y variado
            similarity_boost: 0.8,        // Equilibrio para permitir más entonación natural
            style: 0.55,                  // Mayor estilo para ser más expresivo ("cuentacuentos")
            use_speaker_boost: true       // Claridad
        },
        language: 'es'                    // Español explícito
    },

    // State
    cache: new Map(),
    audioPlayer: null,
    currentAudio: null,
    isPlaying: false,
    currentSequenceId: 0,
    useElevenLabs: true, // Flag to enable/disable high-quality audio system
    allowExternalAPI: false, // DISABLED: Force using local files only

    /**
     * Phonetic corrections for problematic words
     */
    phoneticCorrections: {
        'CUMPLEAÑOS': 'cum-ple-á-ños',
        'cumpleaños': 'cum-ple-á-ños',
        'Frutería': 'fru-te-rí-a',
        'frutería': 'fru-te-rí-a',
        'MATEMÁTICOS DE 1º': 'MATEMÁTICOS DE PRIMERO',
        '1º': 'PRIMERO',
        'aprendiz': 'apren-díz',
        'detectives': 'detectíves',
        'DETECTIVES': 'DETECTÍVES',
        'SUMA': 'suma',
        'RESTA': 'resta',
        'Suma': 'suma',
        'Resta': 'resta',
        'resta': 'resta',
        'restar': 'restar',
    },

    /**
     * Initialize the ElevenLabs TTS module
     */
    init() {
        console.log('🎙️ Inicializando ElevenLabs TTS...');

        // Create audio player
        this.audioPlayer = new Audio();
        this.audioPlayer.onended = () => {
            this.isPlaying = false;
            if (this.onAudioEnd) this.onAudioEnd();
        };

        this.audioPlayer.onerror = (error) => {
            console.error('❌ Error reproduciendo audio:', error);
            this.isPlaying = false;
        };

        // Load cache from localStorage
        this.loadCacheFromStorage();

        console.log('✅ ElevenLabs TTS inicializado');
    },

    /**
     * Clean text from emojis and special characters
     */
    cleanText(text) {
        if (!text) return '';

        // Remove emojis using regex
        let cleaned = text.replace(/[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]/gu, '');

        // Remove specific emojis that might not be caught
        cleaned = cleaned.replace(/[💡⭐✨🎉🎁🏆🕵️‍♀️🕵️‍♂️👦👧🏠🚪🔙←→✕▶🤖🌐🗺️📄✏️📊📖🤔✓★🔊]/gu, '');

        // Apply phonetic corrections for problematic words
        for (const [wrong, correct] of Object.entries(this.phoneticCorrections)) {
            const regex = new RegExp(wrong, 'gi');
            cleaned = cleaned.replace(regex, correct);
        }

        // Replace newlines with spaces
        cleaned = cleaned.replace(/\n/g, ' ');

        // Remove extra spaces
        cleaned = cleaned.trim().replace(/\s+/g, ' ');

        return cleaned;
    },

    /**
     * Generate candidate cache keys from text (handling case variations)
     */
    getCandidateCacheKeys(text) {
        const cleaned = this.cleanText(text);
        if (!cleaned) return [];
        const keys = [this.getCacheKey(cleaned)];
        const lower = cleaned.toLowerCase();
        if (lower !== cleaned) keys.push(this.getCacheKey(lower));
        const firstChar = cleaned.charAt(0);
        const toggledFirst = (firstChar === firstChar.toLowerCase() ? firstChar.toUpperCase() : firstChar.toLowerCase()) + cleaned.slice(1);
        if (toggledFirst !== cleaned && toggledFirst !== lower) keys.push(this.getCacheKey(toggledFirst));
        return keys;
    },

    getCacheKey(text) {
        let hash = 0;
        for (let i = 0; i < text.length; i++) {
            const char = text.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash;
        }
        return `elevenlabs_${Math.abs(hash)}_${this.config.voiceId}`;
    },

    /**
     * Fetch audio from ElevenLabs API
     */
    async fetchFromAPI(text) {
        const url = `${this.config.endpoint}/${this.config.voiceId}`;
        console.log(`🌐 Llamando a ElevenLabs API para: "${text.substring(0, 50)}..."`);

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000);

        try {
            const response = await fetch(url, {
                method: 'POST',
                signal: controller.signal,
                headers: {
                    'Accept': 'audio/mpeg',
                    'Content-Type': 'application/json',
                    'xi-api-key': this.config.apiKey
                },
                body: JSON.stringify({
                    text: text,
                    model_id: this.config.modelId,
                    voice_settings: this.config.voiceSettings,
                    language_code: 'es'
                })
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`API Error ${response.status}: ${errorText}`);
            }

            const audioBlob = await response.blob();
            console.log('✅ Audio recibido desde ElevenLabs');
            return audioBlob;

        } catch (error) {
            console.error('❌ Error al obtener audio de ElevenLabs:', error);
            throw error;
        }
    },

    /**
     * Save audio blob to cache
     */
    async saveToCacheAsync(cacheKey, audioBlob) {
        this.cache.set(cacheKey, audioBlob);
        try {
            const reader = new FileReader();
            reader.onloadend = () => {
                const base64 = reader.result;
                if (base64.length < 1000000) {
                    localStorage.setItem(cacheKey, base64);
                    console.log(`💾 Audio guardado en caché: ${cacheKey}`);
                } else {
                    console.warn('⚠️ Audio demasiado grande para localStorage');
                }
            };
            reader.readAsDataURL(audioBlob);
        } catch (error) {
            console.warn('⚠️ Error guardando en localStorage:', error);
        }
    },

    /**
     * Load cache from localStorage
     */
    loadCacheFromStorage() {
        try {
            const keys = Object.keys(localStorage);
            let loadedCount = 0;

            keys.forEach(key => {
                if (key.startsWith('elevenlabs_')) {
                    try {
                        const base64 = localStorage.getItem(key);
                        const binary = atob(base64.split(',')[1]);
                        const array = [];
                        for (let i = 0; i < binary.length; i++) {
                            array.push(binary.charCodeAt(i));
                        }
                        const blob = new Blob([new Uint8Array(array)], { type: 'audio/mpeg' });
                        this.cache.set(key, blob);
                        loadedCount++;
                    } catch (e) {
                        localStorage.removeItem(key);
                    }
                }
            });

            if (loadedCount > 0) {
                console.log(`💾 ${loadedCount} audios cargados desde caché`);
            }
        } catch (error) {
            console.warn('⚠️ Error cargando caché:', error);
        }
    },

    /**
     * Internal helper to stop audio player without incrementing sequence
     */
    _stopInternal() {
        if (this.audioPlayer) {
            this.audioPlayer.pause();
            this.audioPlayer.currentTime = 0;
        }
    },

    /**
     * Play audio from blob
     */
    playAudio(audioBlob, callback) {
        try {
            if (window.speechSynthesis) window.speechSynthesis.cancel();
            this._stopInternal();

            const audioUrl = URL.createObjectURL(audioBlob);
            this.audioPlayer.src = audioUrl;
            this.isPlaying = true;
            this.onAudioEnd = () => {
                URL.revokeObjectURL(audioUrl);
                if (callback) callback();
            };

            this.audioPlayer.play().catch(error => {
                console.error('❌ Error al reproducir:', error);
                this.isPlaying = false;
                if (callback) callback();
            });

        } catch (error) {
            console.error('❌ Error en playAudio:', error);
            if (callback) callback();
        }
    },

    /**
     * Speak an array of text parts sequentially
     */
    async speakParts(parts, language = 'es-ES', callback = null, triggerBtn = null) {
        if (!parts || parts.length === 0) {
            if (callback) callback();
            return;
        }

        console.log(`🧩 Modular speak request: [${parts.join(' | ')}]`);

        this.currentSequenceId++;
        const sequenceId = this.currentSequenceId;

        if (this.audioPlayer) {
            this.audioPlayer.pause();
            this.audioPlayer.currentTime = 0;
        }
        if (window.speechSynthesis) window.speechSynthesis.cancel();
        this.isPlaying = true;
        this.onAudioEnd = null;

        const seqPlayer = new Audio();

        const speakNext = async () => {
            if (this.currentSequenceId !== sequenceId) {
                console.log('⏹️ Modular sequence interrupted or replaced');
                seqPlayer.pause();
                return;
            }

            if (index >= parts.length) {
                console.log('🏁 Modular sequence finished');
                this.isPlaying = false;
                if (callback) callback();
                return;
            }

            const currentPart = parts[index++];
            console.log(`🎙️ Modular part ${index}/${parts.length}: "${currentPart}"`);
            this.isPlaying = true;

            try {
                const cleaned = this.cleanText(currentPart);
                if (!cleaned || cleaned.length === 0) {
                    speakNext();
                    return;
                }

                const candidateKeys = this.getCandidateCacheKeys(cleaned);
                let audioBlob = null;
                for (const k of candidateKeys) {
                    if (this.cache.has(k)) {
                        audioBlob = this.cache.get(k);
                        break;
                    }
                }

                if (!audioBlob) {
                    for (const keyCandidate of candidateKeys) {
                        const localPaths = [
                            `assets/audio/fixed_part1/${keyCandidate}.mp3`,
                            `assets/audio/fixed_part2/${keyCandidate}.mp3`,
                            `assets/audio/fragments/${keyCandidate}.mp3`,
                            `assets/audio/Audios_Cumpleaños/${keyCandidate}.mp3`,
                            `assets/audio/Audios_Fruteria/${keyCandidate}.mp3`
                        ];

                        for (const localPath of localPaths) {
                            try {
                                if (window.location.protocol === 'file:') {
                                    const isFound = await new Promise((resolve) => {
                                        const tempAudio = new Audio();
                                        tempAudio.oncanplaythrough = () => resolve(true);
                                        tempAudio.onerror = () => resolve(false);
                                        setTimeout(() => resolve(false), 800);
                                        tempAudio.src = localPath;
                                    });

                                    if (isFound) {
                                        let blobOk = false;
                                        try {
                                            const resp = await fetch(localPath);
                                            if (resp.ok) {
                                                audioBlob = await resp.blob();
                                                this.cache.set(keyCandidate, audioBlob);
                                                blobOk = true;
                                                break;
                                            }
                                        } catch (_) {
                                            /* Ignore local cache fetch errors */
                                        }

                                        if (!blobOk) {
                                            await new Promise((resolve) => {
                                                if (this.currentSequenceId !== sequenceId) { resolve(); return; }
                                                seqPlayer.src = localPath;
                                                const onEnd = () => { seqPlayer.removeEventListener('ended', onEnd); seqPlayer.removeEventListener('error', onEnd); resolve(); };
                                                seqPlayer.addEventListener('ended', onEnd);
                                                seqPlayer.addEventListener('error', onEnd);
                                                seqPlayer.play().catch(resolve);
                                            });
                                            speakNext();
                                            return;
                                        }
                                    }
                                } else {
                                    const resp = await fetch(localPath);
                                    if (resp.ok) {
                                        const contentType = resp.headers.get('Content-Type');
                                        if (contentType && contentType.includes('text/html')) continue;
                                        const blob = await resp.blob();
                                        if (blob.size > 500) {
                                            audioBlob = blob;
                                            this.cache.set(keyCandidate, audioBlob);
                                            break;
                                        }
                                    }
                                }
                            } catch (_) {
                                /* Ignore audio load fallback errors */
                            }
                        }
                        if (audioBlob) break;
                    }
                }

                if (!audioBlob && this.allowExternalAPI && navigator.onLine) {
                    console.log(`🌐 Fetching part from API: "${cleaned}"`);
                    audioBlob = await this.fetchFromAPI(cleaned);
                    this.saveToCacheAsync(candidateKeys[0], audioBlob);
                }

                if (audioBlob) {
                    const url = URL.createObjectURL(audioBlob);
                    await new Promise((resolve) => {
                        if (this.currentSequenceId !== sequenceId) { resolve(); return; }
                        seqPlayer.src = url;
                        const onEnd = () => {
                            seqPlayer.removeEventListener('ended', onEnd);
                            seqPlayer.removeEventListener('error', onEnd);
                            URL.revokeObjectURL(url);
                            resolve();
                        };
                        seqPlayer.addEventListener('ended', onEnd);
                        seqPlayer.addEventListener('error', onEnd);
                        seqPlayer.play().catch(() => { URL.revokeObjectURL(url); resolve(); });
                    });
                    speakNext();
                } else {
                    console.warn(`⚠️ Segment "${currentPart}" not found locally. Falling back to native.`);
                    await new Promise((resolve) => {
                        if (this.currentSequenceId !== sequenceId) { resolve(); return; }
                        if (window.speechSynthesis) {
                            window.speechSynthesis.cancel();
                            let nativeText = currentPart;
                            if (window.AudioManager && typeof window.AudioManager.convertNumbersToWords === 'function') {
                                nativeText = window.AudioManager.convertNumbersToWords(nativeText, language);
                            }
                            const utterance = new SpeechSynthesisUtterance(nativeText);
                            utterance.lang = language || 'es-ES';
                            utterance.rate = 0.85;
                            utterance.onend = () => resolve();
                            utterance.onerror = () => resolve();
                            window.speechSynthesis.speak(utterance);
                        } else {
                            resolve();
                        }
                    });
                    speakNext();
                }

            } catch (err) {
                console.error('Modular speak error:', err);
                speakNext();
            }
        };

        let index = 0;
        speakNext();
    },

    /**
     * Main speak function
     */
    async speak(text, language = 'es-ES', callback = null, triggerBtn = null) {
        this.stop();
        if (window.speechSynthesis) window.speechSynthesis.cancel();
        this.isPlaying = true;

        try {
            const cleanedText = this.cleanText(text);
            if (!cleanedText || cleanedText.length === 0) {
                console.warn('⚠️ Texto vacío después de limpiar');
                this.isPlaying = false;
                if (callback) callback();
                return;
            }

            const candidateKeys = this.getCandidateCacheKeys(cleanedText);
            for (const k of candidateKeys) {
                if (this.cache.has(k)) {
                    const audioBlob = this.cache.get(k);
                    this.playAudio(audioBlob, callback);
                    return;
                }
            }

            try {
                for (const keyCandidate of candidateKeys) {
                    const localPaths = [
                        `assets/audio/fixed_part1/${keyCandidate}.mp3`,
                        `assets/audio/fixed_part2/${keyCandidate}.mp3`,
                        `assets/audio/fragments/${keyCandidate}.mp3`,
                        `assets/audio/Audios_Cumpleaños/${keyCandidate}.mp3`,
                        `assets/audio/Audios_Fruteria/${keyCandidate}.mp3`
                    ];

                    for (const localPath of localPaths) {
                        if (await this.tryPlayLocal(localPath, keyCandidate, callback)) return;
                    }
                }

                if (cleanedText.match(/\d+/)) {
                    console.log(`🧩 Texto no encontrado: "${cleanedText}". Intentando combinación modular...`);
                    const parts = this.decomposeTextForModular(cleanedText);
                    if (parts.length > 1) {
                        this.isPlaying = false;
                        return this.speakParts(parts, language, callback, triggerBtn);
                    }
                }

                console.log('ℹ️ No se encontró audio local para:', candidateKeys[0]);
            } catch (e) {
                console.warn('⚠️ Error buscando audio local:', e);
            }

            if (!this.allowExternalAPI) {
                console.info('🚫 External API calls are disabled. Falling back to native voice.');
                this.fallbackToWebSpeech(text, language, callback, triggerBtn);
                return;
            }

            if (!navigator.onLine) {
                console.warn('⚠️ Sin conexión a internet: saltando llamada a API ElevenLabs');
                await new Promise(r => setTimeout(r, 100));
                this.fallbackToWebSpeech(text, language, callback);
                return;
            }

            console.log('🌐 Obteniendo audio de ElevenLabs...');
            const audioBlob = await this.fetchFromAPI(cleanedText);
            await this.saveToCacheAsync(candidateKeys[0], audioBlob);
            this.playAudio(audioBlob, callback);

        } catch (error) {
            this.isPlaying = false;
            console.error('❌ Error en ElevenLabs.speak:', error);
            console.log('🔄 Fallback a Web Speech API');
            this.fallbackToWebSpeech(text, language, callback);
        }
    },

    /**
     * Stop current audio
     */
    stop() {
        this.currentSequenceId++;
        if (this.audioPlayer) {
            this.audioPlayer.pause();
            this.audioPlayer.currentTime = 0;
        }
        this.isPlaying = false;
    },

    /**
     * Fallback to Web Speech API - always in Spanish
     */
    fallbackToWebSpeech(text, language, callback, triggerBtn = null) {
        const lang = language || 'es-ES';
        console.warn(`🔄 Fallback a voz nativa (${lang}): "${text.substring(0, 40)}"`);

        const onEnd = () => {
            this.isPlaying = false;
            if (callback) callback();
        };

        if (window.AudioManager && typeof window.AudioManager.speakWithCallback === 'function') {
            console.log('🗣️ Enrutando fallback nativo a través de AudioManager...');
            window.AudioManager.speakWithCallback(text, lang, onEnd, triggerBtn, { forceNative: true });
        } else if (window.speechSynthesis) {
            console.log('🗣️ Usando window.speechSynthesis.speak directamente (AudioManager no disponible)');
            window.speechSynthesis.cancel();
            const utterance = new SpeechSynthesisUtterance(text);
            utterance.lang = lang;
            utterance.rate = 0.85;
            
            utterance.onend = onEnd;
            utterance.onerror = (e) => {
                console.error('❌ Native TTS Error:', e);
                onEnd();
            };
            window.speechSynthesis.speak(utterance);
        } else {
            console.error('❌ No hay sistema de TTS disponible');
            this.isPlaying = false;
            if (callback) callback();
        }
    },

    /**
     * Tries to play a local audio file if it exists
     */
    async tryPlayLocal(localPath, cacheKey, callback) {
        try {
            if (window.location.protocol === 'file:') {
                const isFound = await new Promise((resolve) => {
                    const tempAudio = new Audio();
                    tempAudio.oncanplaythrough = () => resolve(true);
                    tempAudio.onerror = () => resolve(false);
                    setTimeout(() => resolve(false), 1000);
                    tempAudio.src = localPath;
                });

                if (isFound) {
                    console.log(`✅ [file://] Local audio found: ${localPath}`);
                    try {
                        const resp = await fetch(localPath);
                        if (resp.ok) {
                            const blob = await resp.blob();
                            this.cache.set(cacheKey, blob);
                            this.playAudio(blob, callback);
                            return true;
                        }
                    } catch (e) {
                        this.playFromPath(localPath, callback);
                        return true;
                    }
                }
            }

            const response = await fetch(localPath);
            if (response.ok) {
                const contentType = response.headers.get('Content-Type');
                if (contentType && contentType.includes('text/html')) return false;

                const audioBlob = await response.blob();
                if (audioBlob.size < 500) return false;

                console.debug(`✅ Local audio found: ${localPath} (${audioBlob.size} bytes)`);
                this.cache.set(cacheKey, audioBlob);
                this.playAudio(audioBlob, callback);
                return true;
            }
        } catch (err) {
            console.warn(`⚠️ Error checking local audio ${localPath}:`, err);
        }
        return false;
    },

    /**
     * Helper to play directly from a path without Blob conversion
     */
    playFromPath(path, callback) {
        if (window.speechSynthesis) window.speechSynthesis.cancel();
        this._stopInternal();
        this.isPlaying = true;

        this.audioPlayer.src = path;
        this.onAudioEnd = () => {
            if (callback) callback();
        };
        this.audioPlayer.play().catch(e => {
            console.error('Error playing from path:', e);
            this.isPlaying = false;
            if (callback) callback();
        });
    },

    /**
     * Splits text into fixed parts and numbers for modular playback
     */
    decomposeTextForModular(text) {
        const numberRegex = /(\d+)/g;
        const parts = [];
        let lastIndex = 0;
        let match;

        while ((match = numberRegex.exec(text)) !== null) {
            const before = text.substring(lastIndex, match.index).trim();
            if (before) parts.push(before);
            parts.push(match[0]);
            lastIndex = numberRegex.lastIndex;
        }

        const after = text.substring(lastIndex).trim();
        if (after) parts.push(after);

        return parts;
    },

    /**
     * Clear cache
     */
    clearCache() {
        console.log('🗑️ Limpiando caché de ElevenLabs...');
        this.cache.clear();
        const keys = Object.keys(localStorage);
        keys.forEach(key => {
            if (key.startsWith('elevenlabs_')) {
                localStorage.removeItem(key);
            }
        });
        console.log('✅ Caché limpiado');
    },

    /**
     * Toggle ElevenLabs on/off
     */
    toggle(enabled) {
        this.useElevenLabs = enabled;
        console.log(`🎙️ ElevenLabs ${enabled ? 'activado' : 'desactivado'}`);
    }
};

// Initialize and export
if (typeof window !== 'undefined') {
    window.ElevenLabsTTS = ElevenLabsTTS;
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => ElevenLabsTTS.init());
    } else {
        ElevenLabsTTS.init();
    }
}

export default ElevenLabsTTS;
